import {
  action,
  computed,
  makeObservable,
  observable,
  reaction,
  runInAction,
} from 'mobx';
import { APIMap } from '@datum-group/datum-map/lib';
import { BaseLayersStore } from './BaseLayersStore';
import { Map as MapLink } from '@project/store/LinkController/Map';
import { UserController } from '@project/store/User';

const PUBLIC_WIFI_LAYER = 'public_wifi_s';

export class InitLayersStore {
  @observable private _isLoading = false;
  @observable private _error?: string = undefined;
  private _baseLayers: BaseLayersStore;
  private _apiMap: APIMap;
  private _user: UserController | undefined = undefined;

  constructor(
    baseLayers: BaseLayersStore,
    apiMap: APIMap,
    user?: UserController,
  ) {
    this._baseLayers = baseLayers;
    this._apiMap = apiMap;
    this._user = user;

    makeObservable(this);

    reaction(
      () => this._user,
      () => {
        if (this._user?.value.status === 'loaded') {
          this.load();
        }
      },
    );
  }

  @computed get isLoading() {
    return this._isLoading;
  }

  @computed get error() {
    return this._error;
  }

  @action async load() {
    this._isLoading = true;
    this._error = undefined;

    try {
      await this._baseLayers.load();

      const remember = localStorage.getItem('rememberLayerState') === 'true';
      const saved = localStorage.getItem('selectedLayers');
      const isLoaded = this._user?.value.status === 'loaded';

      if (remember && saved && isLoaded) {
        try {
          const selected = JSON.parse(saved) as string[];

          const baseLayer = selected.find((name) =>
            this._baseLayers.isBaseLayer(name),
          );

          if (baseLayer) {
            this._baseLayers.setActiveLayer(baseLayer);
          }

          const overlayLayers = selected.filter(
            (name) => !this._baseLayers.isBaseLayer(name),
          );

          overlayLayers.push(PUBLIC_WIFI_LAYER);

          await Promise.all(
            overlayLayers.map((name) => {
              const existing = this._baseLayers.currentLayerNames;
              if (existing.includes(name)) {
                return Promise.resolve();
              }

              return this._apiMap.addLayer(name);
            }),
          );
        } catch (e) {
          console.warn('Ошибка восстановления слоёв из localStorage', e);
          await this._initFromMapLink();
        }
      } else {
        await this._initFromMapLink();
      }
    } catch (e) {
      runInAction(
        () => (this._error = 'Не удалось загрузить начальные данные'),
      );
    } finally {
      runInAction(() => (this._isLoading = false));
    }
  }

  @action private async _initFromMapLink() {
    const layersToAdd = MapLink.getInitData()?.layersName;

    const uniqueLayersNames = [...new Set(layersToAdd)];
    if (!uniqueLayersNames.includes(PUBLIC_WIFI_LAYER)) {
      uniqueLayersNames.push(PUBLIC_WIFI_LAYER);
    }

    const baseLayerName = uniqueLayersNames.find((layerName) =>
      this._baseLayers.isBaseLayer(layerName),
    );

    if (baseLayerName) {
      this._baseLayers.setActiveLayer(baseLayerName);
      const index = uniqueLayersNames.findIndex(
        (item) => item === baseLayerName,
      );
      uniqueLayersNames.splice(index, 1);
    } else {
      // ToDo: Вынести в env наименование дефолтного слоя
      this._baseLayers.setActiveLayer('Donland_gisro_map');
    }

    await Promise.all(
      uniqueLayersNames.map((layer) => {
        this._apiMap.addLayer(layer);
      }),
    );
  }
}
